import { ThunkDispatch } from 'redux-thunk';

import { OffersApi } from 'api/index';
import { HumanReadableError } from 'shared/errors/HumanReadableError';
import { createAction } from 'shared/util/utility';
import { BonusInUseError } from '../errors/BonusInUseError';
import { Bonus, Offer } from '../interface/offers.interface';
import * as actionTypes from './action-types';

const setBonusesAndFreebets = (bonusesActive: Offer[], bonusesQueued: Offer[], freebetsActive: Offer[]) => {
	return createAction(actionTypes.FETCH_BONUSES_AND_FREEBETS_SUCCESS, { bonusesActive, bonusesQueued, freebetsActive });
};

const clearBonusesAndFreebets = () => {
	return createAction(actionTypes.FETCH_BONUSES_AND_FREEBETS_FAIL);
};

const setBonuses = (bonusesQueued: Offer[], bonusesActive: Offer[] = []) => {
	return createAction(actionTypes.FETCH_BONUSES_SUCCESS, { bonusesActive, bonusesQueued });
};

export const fetchBonusesAndFreebets = () => {
	return async (dispatch: ThunkDispatch<{}, {}, any>) => {
		try {
			dispatch(createAction(actionTypes.FETCH_BONUSES_AND_FREEBETS_INIT));
			const bonusesAndFreebets = await OffersApi.getBonusesAndFreebets({
				offset: 0,
				status: 'ACTIVE,QUEUE',
			});

			// TODO: bz: maybe we should handle errors somewhere closer to the axios?

			if (!bonusesAndFreebets || bonusesAndFreebets.status >= 400) {
				throw new Error(`Error fetching bonuses and freebets (empty response or error status)`);
			}

			if (bonusesAndFreebets.data.isError && bonusesAndFreebets.data.code !== 421) {
				throw new Error(`Error fetching bonuses and freebets (data.isError: ${bonusesAndFreebets.data.message})`);
			}

			dispatch(
				setBonusesAndFreebets(
					bonusesAndFreebets.data.data?.bonusList.active || [],
					bonusesAndFreebets.data.data?.bonusList.queue || [],
					bonusesAndFreebets.data.data?.freebetList.active || [],
				),
			);

			return bonusesAndFreebets.data;
		} catch (err) {
			dispatch(clearBonusesAndFreebets());
			console.error(err);
		}
	};
};

export const rejectOfferAction = (offerId: number) => {
	return async (dispatch: ThunkDispatch<{}, {}, any>) => {
		const response = await OffersApi.rejectBonus(offerId);

		if (!response || response.status >= 400) {
			throw new Error(`Error fetching bonuses and freebets (empty response or error status)`);
		}

		if (response.data.isError) {
			throw new HumanReadableError(response.data?.message);
		}

		if (!response.data.data.bonusList.active.length) {
			dispatch(fetchBonusesAndFreebets());
			dispatch(setBonuses(response.data.data.bonusList.queue || []));
		} else {
			dispatch(setBonuses(response.data.data.bonusList.queue || [], response.data.data.bonusList.active || []));
		}
	};
};

export const activateOfferAction = (offerId: number) => {
	return async (dispatch: ThunkDispatch<{}, {}, any>) => {
		const response = await OffersApi.activateBonus(offerId);

		if (!response || response.status >= 400) {
			throw new Error(`Error fetching bonuses and freebets (empty response or error status)`);
		}

		if (response.data.isError) {
			throw new HumanReadableError(response.data?.message);
		}

		const queuedBonuses = response.data.data.bonusList.queue.filter((bonus: Bonus) => {
			return bonus.offerId != offerId;
		});

		dispatch(setBonuses(queuedBonuses || [], response.data.data.bonusList.active || []));
	};
};

export const waiveBonusAction = (offerId: number) => {
	return async (dispatch: ThunkDispatch<{}, {}, any>): Promise<any> => {
		try {
			const response = await OffersApi.waiveBonus(offerId);

			if (!response || response.status >= 400) {
				throw new Error('Failed to waive bonus');
			}

			if (response.data?.code === 904) {
				throw new BonusInUseError();
			}

			if (response.data?.isError) {
				throw new HumanReadableError(response.data.message || 'Unexpected error');
			}

			await dispatch(fetchBonusesAndFreebets());
		} catch (error) {
			console.error('waiveBonusAction error:', error);
			throw error;
		}
	};
};
